import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CalendarIcon,
  PlusIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';
import '../../styles/components.css';
import { useAuth } from '../../context/AuthContext';
import { useProgressNotification } from '../../hooks/useProgressNotification';
import ProgressNotification from '../common/ProgressNotification';
import DeleteOverlay from '../common/DeleteOverlay';
import { formatDate, isSameDay } from '../../utils/dateUtils';
import { useConcessionTypes, poolDisplayName } from '../../utils/concessions';
import AddClassModal from './components/AddClassModal';

const startOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const endOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
};

const eachDayOfInterval = ({ start, end }) => {
  const days = [];
  const current = new Date(start);
  const endDate = new Date(end);
  
  while (current <= endDate) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return days;
};

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const subMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
};

const isSameMonth = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
};

const ManageClasses = () => {
  const { user, isAdmin } = useAuth();
  const { types: concessionTypes } = useConcessionTypes(true);
  const {
    notification,
    showProgress,
    completeProgress,
    errorProgress,
    hideProgress
  } = useProgressNotification();



  const handleClassClick = (classItem) => {
    setSelectedClass(classItem);
    if (user?.canEdit()) {
      setEditForm({
        name: classItem.name,
        description: classItem.description,
        duration: classItem.duration,
        instructor: classItem.instructor,
        location: classItem.location,
        room: classItem.room,
        maxCapacity: classItem.maxCapacity,
        concessionsRequired: classItem.concessionsRequired !== undefined ? Number(classItem.concessionsRequired) : 1,
        concessionType: classItem.concessionType || '',
        startTime: formatDate(classItem.startTime, 'yyyy-MM-dd\'T\'HH:mm'),
        scheduleType: classItem.scheduleType,
        recurringPattern: classItem.recurringPattern || 'none',
        recurringStartDate: classItem.recurringStartDate ? formatDate(classItem.recurringStartDate, 'yyyy-MM-dd') : '',
        recurringEndDate: classItem.recurringEndDate ? formatDate(classItem.recurringEndDate, 'yyyy-MM-dd') : '',
        recurringDays: classItem.recurringDays || [],
        recurringInterval: classItem.recurringInterval || 1
      });
      setShowEditModal(true);
    } else {
      setShowViewModal(true);
    }
  };
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalInitialStartTime, setAddModalInitialStartTime] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isCreatingClass, setIsCreatingClass] = useState(false);
  const [isDeletingClass, setIsDeletingClass] = useState(false);
  
  
  // Calendar state - initialize to first of month to avoid day overflow issues
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Calendar navigation - defined after state, using functional updates
  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth(prev => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(prev => addMonths(prev, 1));
  }, []);

  // Memoized calendar days - only recalculates when month changes
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = [];
    const firstDayOfWeek = monthStart.getDay();
    const mondayOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < mondayOffset; i++) {
      days.push(null);
    }
    const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
    days.push(...monthDays);
    return days;
  }, [currentMonth]);

  // Memoized classes-by-day map - only recalculates when classes change
  const classesByDay = useMemo(() => {
    const map = new Map();
    classes.forEach(classItem => {
      const dateKey = formatDate(classItem.startTime, 'yyyy-MM-dd');
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey).push(classItem);
    });
    return map;
  }, [classes]);

  // Helper to get classes for a specific day using the memoized map
  const getClassesForDay = useCallback((day) => {
    const dateKey = formatDate(day, 'yyyy-MM-dd');
    return classesByDay.get(dateKey) || [];
  }, [classesByDay]);

  const fetchClasses = useCallback(async () => {
    try {
      // Ensure the token is set in axios headers
      const token = localStorage.getItem('token');
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get('/api/admin/classes');
      setClasses(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error(`Failed to fetch classes: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch classes if user is authenticated and is admin
    if (user && isAdmin) {
      fetchClasses();
    } else if (user && !isAdmin) {
      setLoading(false);
    }
  }, [user, isAdmin, fetchClasses]);

  // Removed unused handleEditClass function

  const handleSaveEdit = async () => {
    try {
      
      // Validate required fields
      if (!editForm.name || !editForm.instructor || !editForm.location || !editForm.startTime) {
        toast.error('Please fill in all required fields: Name, Instructor, Location, and Start Time');
        return;
      }
      
      // Validate startTime format
      const startDate = new Date(editForm.startTime);
      if (isNaN(startDate.getTime())) {
        toast.error('Invalid start time format. Please select a valid date and time.');
        return;
      }
      
      // Validate recurring schedule fields if schedule type is recurring
      if (editForm.scheduleType === 'recurring') {
        if (!editForm.startTime) {
          toast.error('Please select a start time for recurring schedule');
          return;
        }
        
        if (editForm.recurringPattern === 'custom' && editForm.recurringDays.length === 0) {
          toast.error('Please select at least one day for custom recurring pattern');
          return;
        }
        
        // Only validate end date if it's provided and not empty
        if (editForm.recurringEndDate && editForm.recurringEndDate.trim() !== '' && editForm.recurringEndDate !== 'Invalid date' && new Date(editForm.recurringEndDate) <= new Date(editForm.startTime)) {
          toast.error('End date must be after start date');
          return;
        }
      }
      
      // Prepare form data for API - only include editable fields
      const formData = {
        name: editForm.name,
        description: editForm.description,
        instructor: editForm.instructor,
        location: editForm.location,
        duration: editForm.duration,
        maxCapacity: editForm.maxCapacity,
        concessionsRequired: editForm.concessionsRequired,
        concessionType: editForm.concessionType || '',
        isActive: editForm.isActive
      };

      if (!formData.concessionType) {
        toast.error('Please choose a concession type for this class');
        return;
      }
      
      // Note: All scheduling fields (startTime, scheduleType, recurringPattern, etc.) are now immutable
      // and cannot be changed once a class is created. Admins must create a new class to change scheduling.
      
      const response = await axios.put(`/api/admin/classes/${selectedClass._id}`, formData);

      if (response.status === 200) {
        // Use the backend's success message
        const successMessage = response.data.message || 'Class updated successfully';
        toast.success(successMessage);
        setShowEditModal(false);
        // Update the classes array with the response data
        const updatedClass = response.data.class;
        setClasses(prevClasses => 
          prevClasses.map(c => c._id === updatedClass._id ? updatedClass : c)
        );
        fetchClasses(); // This will refresh the data
      } else {
        toast.error('Failed to update class');
      }
    } catch (error) {
      console.error('Error updating class:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
      toast.error('Failed to update class');
      }
    }
  };

  // Memoized close handler for AddClassModal
  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
  }, []);

  const handleAddClass = useCallback(async (addForm) => {
    setIsCreatingClass(true);
    
    // Show progress notification
    const isRecurring = addForm.scheduleType === 'recurring';
    const estimatedInstances = isRecurring ? 52 : 1;
    showProgress(
      isRecurring ? 'Creating Recurring Classes' : 'Creating Class',
      estimatedInstances
    );
    
    try {
      // Validate required fields
      if (!addForm.name || !addForm.instructor || !addForm.location || !addForm.startTime) {
        toast.error('Please fill in all required fields: Name, Instructor, Location, and Start Time');
        setIsCreatingClass(false);
        return;
      }
      
      // Validate recurring schedule fields
      if (addForm.scheduleType === 'recurring') {
        if (!addForm.startTime) {
          toast.error('Please select a start time for recurring schedule');
          setIsCreatingClass(false);
          return;
        }
        
        if (addForm.recurringPattern === 'custom' && addForm.recurringDays.length === 0) {
          toast.error('Please select at least one day for custom recurring pattern');
          setIsCreatingClass(false);
          return;
        }
        
        if (addForm.recurringEndDate && new Date(addForm.recurringEndDate) <= new Date(addForm.startTime)) {
          toast.error('End date must be after start date');
          setIsCreatingClass(false);
          return;
        }
      }
      
      // Ensure the token is set in axios headers
      const token = localStorage.getItem('token');
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Prepare form data for API
      const formData = { ...addForm };
      
      // For recurring classes, extract date from startTime for recurringStartDate
      if (formData.scheduleType === 'recurring' && formData.startTime) {
        formData.recurringStartDate = formData.startTime.split('T')[0];
      }
      
      const response = await axios.post('/api/admin/classes', formData);

      if (response.status === 201) {
        completeProgress();
        setShowAddModal(false);
        toast.success(isRecurring ? 'Recurring classes created successfully' : 'Class created successfully');
        fetchClasses();
        setTimeout(() => hideProgress(), 3000);
      } else {
        errorProgress('Failed to create class');
        toast.error('Failed to create class');
      }
    } catch (error) {
      console.error('Error creating class:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create class';
      errorProgress(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsCreatingClass(false);
    }
  }, [showProgress, completeProgress, errorProgress, hideProgress, fetchClasses]);

  const handleDeleteClass = async (classId, deleteType = 'single') => {
    setIsDeletingClass(true);
    
    try {
      const response = await axios.delete(`/api/admin/classes/${classId}`, {
        data: { deleteType }
      });

      if (response.status === 200) {
        // Close modal immediately
        setShowDeleteConfirmModal(false);
        setSelectedClass(null);
        
        // Show success message
        const isRecurring = deleteType === 'all-future';
        toast.success(
          isRecurring 
            ? 'All future classes deleted successfully!' 
            : 'Class deleted successfully!'
        );
        
        // Refresh classes list
        fetchClasses();
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      
      // Show error toast
      toast.error(error.response?.data?.message || 'Failed to delete class');
    } finally {
      setIsDeletingClass(false);
    }
  };

  const handleDeleteClassClick = (classItem) => {
    // Always use the custom modal for consistency
    setShowDeleteConfirmModal(true);
    setSelectedClass(classItem);
  };

  const checkIfRecurringClass = (classItem) => {
    // Check if there are other classes with the same name, instructor, and location
    // on different dates - this suggests it's part of a recurring schedule
    const sameClasses = classes.filter(cls => 
      cls._id !== classItem._id &&
      cls.name === classItem.name &&
      cls.instructor === classItem.instructor &&
      cls.location === classItem.location
    );
    
    return sameClasses.length > 0;
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full w-12 h-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user && !isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-xl font-semibold mb-2">Access Denied</div>
          <p className="text-secondary">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">
              {user?.canEdit() ? 'Manage Classes' : 'View Classes'}
            </h1>
            <p className="text-secondary mt-1">
              {user?.canEdit() ? 'View and manage fitness classes' : 'View fitness classes'}
            </p>
          </div>
          
          {user?.canEdit() && (
            <button
              onClick={() => {
                setAddModalInitialStartTime('');
                setShowAddModal(true);
              }}
              className="btn-primary mt-4 btn-center-content"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Class
            </button>
          )}
        </div>
      </div>

      {/* Search and Month Navigation */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 w-full">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted" />
            <input
              type="text"
              placeholder="Search classes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

        </div>
      </div>

      {/* Calendar View */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        
        {/* Current Month/Year Display with Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={handlePreviousMonth}
            className="icon-button"
            title="Previous Month"
          >
            <ChevronLeftIcon className="w-5 h-5 text-secondary" />
          </button>
          
          <h2 className="text-lg font-semibold text-primary">
            {formatDate(currentMonth, 'MMMM yyyy')}
          </h2>
          
          <button
            type="button"
            onClick={handleNextMonth}
            className="icon-button"
            title="Next Month"
          >
            <ChevronRightIcon className="w-5 h-5 text-secondary" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="calendar-grid border border-primary rounded-lg overflow-hidden">
          {/* Calendar Days */}
          {calendarDays.map((day, index) => {
            // Handle empty slots (null values)
            if (day === null) {
              return (
                <div
                  key={`empty-${index}`}
                  className={`calendar-day-card calendar-empty-slot p-2 border-b border-primary ${
                    (index + 1) % 7 === 0 ? '' : 'border-r border-primary'
                  }`}
                >
                  {/* Empty slot */}
          </div>
              );
            }
            
            const dayClasses = getClassesForDay(day).filter(classItem => {
              if (searchTerm) {
                return classItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       classItem.instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       classItem.location.toLowerCase().includes(searchTerm.toLowerCase());
              }
              return true;
            });
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, new Date());
            const isFirstOfMonth = index === 0 || (calendarDays[index - 1] && formatDate(day, 'MMM yyyy') !== formatDate(calendarDays[index - 1], 'MMM yyyy'));
            
            return (
              <div
                key={day.toISOString()}
                className={`calendar-day-card p-2 border-b border-primary ${
                  (index + 1) % 7 === 0 ? '' : 'border-r border-primary'
                } ${
                  isToday ? 'calendar-today' : 'bg-card'
                }`}
              >
                {/* Date Header */}
                <div className="flex justify-between items-start mb-2">
                  <div className="text-xs font-medium text-tertiary">
                    {formatDate(day, 'MMM').toUpperCase()}
                    {isFirstOfMonth && formatDate(day, 'yyyy') !== formatDate(new Date(), 'yyyy') && ` ${formatDate(day, 'yyyy')}`}
        </div>
                  <div className="text-xs font-medium text-tertiary">
                    {formatDate(day, 'EEE d').toUpperCase()}
                  </div>
                </div>

                {/* Classes */}
                {dayClasses.length > 0 ? (
                  <div className="space-y-0">
                    {dayClasses.map((classItem, classIndex) => {
                      const isPast = new Date(classItem.startTime) < new Date();
                      return (
                        <div
                          key={classItem._id}
                          onClick={() => handleClassClick(classItem)}
                          className={`text-xs text-secondary transition-all duration-200 p-1 rounded relative ${
                            classIndex < dayClasses.length - 1 ? 'border-b border-primary' : ''
                          } ${isPast ? 'calendar-class-past' : 'calendar-class-current'} cursor-pointer`}
                        >
                          <div 
                            className="flex-1 rounded transition-all duration-200 hover:bg-hover p-2 calendar-class-card relative flex flex-col"
                          >
                            <div className="font-medium mb-1">
                              {classItem.name}
                            </div>
                            <div className="text-muted mb-1" style={{ fontSize: '11px' }}>
                              {classItem.instructor}
                            </div>
                            <div className="text-tertiary mb-1">
                              {(() => {
                                const date = new Date(classItem.startTime);
                                // Since we now store actual date/time in UTC, we can display it directly
                                return formatDate(date, 'h:mm a');
                              })()}
                            </div>
                            {/* Schedule Type Badge */}
                            <div className="text-xs flex items-center justify-between">
                              <span className={`badge ${
                                classItem.scheduleType === 'recurring' 
                                  ? 'badge-info' 
                                  : 'badge-success'
                              }`}>
                                {classItem.scheduleType === 'recurring' ? 'Recurring' : 'One-off'}
                              </span>
                              <PencilIcon className="w-3 h-3 text-secondary" />
                            </div>
              </div>
              </div>
                      );
                    })}
                </div>
                ) : (
                  <div className="text-xs text-muted text-center mt-4">
                    {/* Empty state - no text needed for Google Calendar style */}
                </div>
                )}

                {/* Add Class Button */}
                {isCurrentMonth && user?.canEdit() && (
                  <div className="mt-2 flex justify-center">
                    <button
                      onClick={() => {
                        // Pre-populate date and set default time to 9AM
                        const year = day.getFullYear();
                        const month = String(day.getMonth() + 1).padStart(2, '0');
                        const dayOfMonth = String(day.getDate()).padStart(2, '0');
                        const dateTimeValue = `${year}-${month}-${dayOfMonth}T09:00`;
                        setAddModalInitialStartTime(dateTimeValue);
                        setShowAddModal(true);
                      }}
                      className="icon-button icon-button-sm"
                      title="Add Class"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
              </div>
                )}
                </div>
            );
          })}
              </div>

        {/* Footer Navigation - Same as Header */}
        <div className="flex items-center justify-between mt-6 pt-4">
          <button
            type="button"
            onClick={handlePreviousMonth}
            className="icon-button"
            title="Previous Month"
          >
            <ChevronLeftIcon className="w-5 h-5 text-secondary" />
          </button>
          
          <h2 className="text-lg font-semibold text-primary">
            {formatDate(currentMonth, 'MMMM yyyy')}
          </h2>
          
          <button
            type="button"
            onClick={handleNextMonth}
            className="icon-button"
            title="Next Month"
          >
            <ChevronRightIcon className="w-5 h-5 text-secondary" />
          </button>
        </div>

        {/* Empty State */}
        {classes.length === 0 && (
          <div className="text-center py-12">
            <CalendarIcon className="mx-auto h-12 w-12 text-muted" />
            <h3 className="mt-2 text-sm font-medium text-primary">No classes found</h3>
            <p className="mt-1 text-sm text-secondary">
              {searchTerm 
                ? 'Try adjusting your search.' 
                : 'Get started by creating a new class.'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <button
                  onClick={() => {
                    setAddModalInitialStartTime('');
                    setShowAddModal(true);
                  }}
                  className="btn-primary"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Class
                </button>
            </div>
            )}
        </div>
      )}
      </div>
    </div>

      {/* Edit Class Modal */}
      {showEditModal && user?.canEdit() && (
        <div 
          className="modal-overlay-mobile"
          onClick={() => setShowEditModal(false)}
        >
          <div 
            className="modal-container-mobile"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">Edit Class</h3>
            </div>
            
            <div className="modal-body-mobile">
              <div className="form-group">
                <div>
                  <label className="form-label-small text-secondary">Class Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    rows={3}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg resize-vertical"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Instructor</label>
                  <input
                    type="text"
                    value={editForm.instructor}
                    onChange={(e) => setEditForm({...editForm, instructor: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Location</label>
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="form-label-small text-secondary">Start Time</label>
                  <input
                    type="datetime-local"
                    value={editForm.startTime}
                    disabled
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg immutable-field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ Start time cannot be changed. Create a new class to change the schedule.
                  </p>
                </div>
                
                {/* Schedule Type Selection */}
                  <div>
                  <label className="form-label-small text-secondary">Schedule Type</label>
                  <select
                    value={editForm.scheduleType}
                    disabled
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg immutable-field"
                  >
                    <option value="one-off">One-off Class</option>
                    <option value="recurring">Recurring Schedule</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ Schedule type cannot be changed. Create a new class to change the schedule.
                  </p>
                </div>

                {/* Recurring Schedule Fields - Only show when scheduleType is 'recurring' */}
                {editForm.scheduleType === 'recurring' && (
                  <>
                    <div>
                      <label className="form-label-small text-secondary">Recurring Pattern</label>
                    <select
                      value={editForm.recurringPattern}
                      disabled
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg immutable-field"
                    >
                        <option value="none">None (One-off)</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="custom">Custom (Specific Days)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        ⚠️ Recurring pattern cannot be changed. Create a new class to change the schedule.
                      </p>
                    </div>

                    {/* Custom Days Selection */}
                    {editForm.recurringPattern === 'custom' && (
                      <div>
                        <label className="form-label-small text-secondary">Select Days</label>
                        <div className="checkbox-group">
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                            <label key={day} className="checkbox-item">
                              <input
                                type="checkbox"
                                checked={editForm.recurringDays.includes(day)}
                                disabled
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-500 capitalize">{day}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          ⚠️ Recurring days cannot be changed. Create a new class to change the schedule.
                        </p>
                      </div>
                    )}

                    {/* Interval Selection */}
                    {(editForm.recurringPattern === 'daily' || editForm.recurringPattern === 'weekly' || editForm.recurringPattern === 'monthly') && (
                      <div>
                        <label className="form-label-small text-secondary">
                          Every {editForm.recurringPattern === 'daily' ? 'day' : editForm.recurringPattern === 'weekly' ? 'week' : 'month'}
                        </label>
                    <input
                      type="number"
                      value={editForm.recurringInterval}
                      disabled
                      min="1"
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg immutable-field"
                    />
                        <p className="text-xs text-gray-500 mt-1">
                          ⚠️ Recurring interval cannot be changed. Create a new class to change the schedule.
                        </p>
                      </div>
                    )}

                    {/* Recurring End Date */}
                    <div>
                      <label className="form-label-small text-secondary">End Date (Optional - maximum 1 year)</label>
                    <input
                      type="date"
                      value={editForm.recurringEndDate}
                      disabled
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg immutable-field"
                    />
                      <p className="text-xs text-gray-500 mt-1">
                        ⚠️ End date cannot be changed. Create a new class to change the schedule.
                      </p>
                    </div>
                  </>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label-small text-secondary">Duration (min)</label>
                    <input
                      type="number"
                      value={editForm.duration}
                      onChange={(e) => setEditForm({...editForm, duration: parseInt(e.target.value)})}
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label-small text-secondary">Max Capacity</label>
                    <input
                      type="number"
                      value={editForm.maxCapacity}
                      onChange={(e) => setEditForm({...editForm, maxCapacity: parseInt(e.target.value)})}
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    />
                  </div>
                </div>
                
                
                
                <div>
                  <label className="form-label-small text-secondary">Number of Concessions</label>
                  <input
                    type="number"
                          min="1"
                    value={editForm.concessionsRequired !== undefined ? Number(editForm.concessionsRequired) : 1}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setEditForm({...editForm, concessionsRequired: isNaN(value) ? 1 : value});
                    }}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>

                <div>
                  <label className="form-label-small text-secondary">Concession Type *</label>
                  <select
                    value={editForm.concessionType || ''}
                    onChange={(e) => setEditForm({...editForm, concessionType: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  >
                    <option value="">— required —</option>
                    {concessionTypes.filter(t => !t.isShared).map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.displayName}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-secondary mt-1">
                    Every class draws from a named wallet. Changing this affects future bookings only.
                  </p>
                </div>
                
                      </div>
                      </div>
              
            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary mr-4"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    handleDeleteClassClick(selectedClass);
                  }}
                  className="btn-danger mr-4"
                >
                  Delete Class
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Class Modal */}
      {showViewModal && (
        <div 
          className="modal-overlay-mobile"
          onClick={() => setShowViewModal(false)}
        >
          <div 
            className="modal-container-mobile"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">Class Details</h3>
            </div>
            
            <div className="modal-body-mobile">
              {selectedClass && (
                <div className="space-y-4">
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Class Name</label>
                    <div className="text-primary font-medium">{selectedClass.name}</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Description</label>
                    <div className="text-primary">{selectedClass.description || 'No description provided'}</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Duration</label>
                    <div className="text-primary">{selectedClass.duration} minutes</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Instructor</label>
                    <div className="text-primary">{selectedClass.instructor}</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Location</label>
                    <div className="text-primary">{selectedClass.location}</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Room</label>
                    <div className="text-primary">{selectedClass.room || 'Not specified'}</div>
                  </div>
                  
                  
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Max Capacity</label>
                    <div className="text-primary">{selectedClass.maxCapacity} participants</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Concessions Required</label>
                    <div className="text-primary">{selectedClass.concessionsRequired || 1}</div>
                  </div>

                  <div className="form-group">
                    <label className="form-label-small text-secondary">Concession Type</label>
                    <div className="text-primary">
                      {poolDisplayName(concessionTypes, selectedClass.concessionType)}
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Start Time</label>
                    <div className="text-primary">{formatDate(selectedClass.startTime, 'MMM dd, yyyy • h:mm a')}</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">End Time</label>
                    <div className="text-primary">{formatDate(selectedClass.endTime, 'MMM dd, yyyy • h:mm a')}</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-small text-secondary">Schedule Type</label>
                    <div className="text-primary">{selectedClass.scheduleType || 'Single class'}</div>
                  </div>
                  
                  {selectedClass.scheduleType === 'recurring' && (
                    <>
                      <div className="form-group">
                        <label className="form-label-small text-secondary">Recurring Pattern</label>
                        <div className="text-primary">{selectedClass.recurringPattern}</div>
                      </div>
                      
                      {selectedClass.recurringStartDate && (
                        <div className="form-group">
                          <label className="form-label-small text-secondary">Recurring Start Date</label>
                          <div className="text-primary">{formatDate(selectedClass.recurringStartDate, 'MMM dd, yyyy')}</div>
                        </div>
                      )}
                      
                      {selectedClass.recurringEndDate && (
                        <div className="form-group">
                          <label className="form-label-small text-secondary">Recurring End Date</label>
                          <div className="text-primary">{formatDate(selectedClass.recurringEndDate, 'MMM dd, yyyy')}</div>
                        </div>
                      )}
                      
                      {selectedClass.recurringDays && selectedClass.recurringDays.length > 0 && (
                        <div className="form-group">
                          <label className="form-label-small text-secondary">Recurring Days</label>
                          <div className="text-primary">{selectedClass.recurringDays.join(', ')}</div>
                        </div>
                      )}
                      
                      <div className="form-group">
                        <label className="form-label-small text-secondary">Recurring Interval</label>
                        <div className="text-primary">Every {selectedClass.recurringInterval} {selectedClass.recurringPattern}</div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Class Modal - Extracted to separate component for performance */}
      {user?.canEdit() && (
        <AddClassModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          onSubmit={handleAddClass}
          isCreating={isCreatingClass}
          initialStartTime={addModalInitialStartTime}
        />
      )}



      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && user?.canEdit() && (
        <div className="modal-overlay-mobile" onClick={() => setShowDeleteConfirmModal(false)}>
          <div className="modal-container-mobile" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">Delete Class</h3>
                </div>
                
            <div className="modal-body-mobile" style={{ position: 'relative' }}>
              {isDeletingClass && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-2"></div>
                    <p className="text-red-600 font-medium">
                      {checkIfRecurringClass(selectedClass) 
                        ? 'Deleting recurring classes... This may take a moment.' 
                        : 'Deleting class...'}
                    </p>
                  </div>
                </div>
              )}
              <div className="text-center mb-6">
                <p className="text-secondary mb-4">
                  {checkIfRecurringClass(selectedClass) 
                    ? "This class appears to be part of a recurring schedule. How would you like to proceed?"
                    : "Are you sure you want to delete this class? This action cannot be undone."
                  }
                </p>
                <div className="class-details-notice">
                  <p>
                    <strong>Class:</strong> {selectedClass?.name}<br/>
                    <strong>Instructor:</strong> {selectedClass?.instructor}<br/>
                    <strong>Date:</strong> {selectedClass?.startTime ? formatDate(new Date(selectedClass.startTime), 'PPP') : 'N/A'}
                  </p>
                </div>
                </div>
                </div>

            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
                <button
                  onClick={() => setShowDeleteConfirmModal(false)}
                  className="btn-secondary mr-4"
                  disabled={isDeletingClass}
                >
                  Cancel
                </button>
                {checkIfRecurringClass(selectedClass) ? (
                  <>
                    <button
                      onClick={() => {
                        setShowDeleteConfirmModal(false);
                        handleDeleteClass(selectedClass._id, 'single');
                      }}
                      className="btn-warning mr-4"
                      disabled={isDeletingClass}
                    >
                      {isDeletingClass ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Deleting...
                        </>
                      ) : (
                        'Delete This Class Only'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirmModal(false);
                        handleDeleteClass(selectedClass._id, 'all-future');
                      }}
                      className="btn-danger"
                      disabled={isDeletingClass}
                    >
                      {isDeletingClass ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Deleting All...
                        </>
                      ) : (
                        'Delete All Future Classes'
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setShowDeleteConfirmModal(false);
                      handleDeleteClass(selectedClass._id, 'single');
                    }}
                    className="btn-danger"
                    disabled={isDeletingClass}
                  >
                    {isDeletingClass ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      'Delete Class'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress Notification */}
      <ProgressNotification
        isVisible={notification.isVisible}
        title={notification.title}
        progress={notification.progress}
        total={notification.total}
        isComplete={notification.isComplete}
        isError={notification.isError}
        errorMessage={notification.errorMessage}
        onClose={hideProgress}
      />
      
      {/* Delete Overlay */}
      <DeleteOverlay 
        isVisible={isDeletingClass}
        message="Deleting classes - won't be a moment"
      />
    </>
  );
};

export default ManageClasses;

